import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ScheduledSubmissions = () => {
  const { token } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    from_date: '',
    to_date: ''
  });

  // Edit dialog
  const [editDialog, setEditDialog] = useState({
    open: false,
    submission: null,
    newDateTime: ''
  });

  useEffect(() => {
    fetchScheduledSubmissions();
  }, [filters]);

  const fetchScheduledSubmissions = async () => {
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.from_date) queryParams.append('from_date', filters.from_date);
      if (filters.to_date) queryParams.append('to_date', filters.to_date);
      
      const response = await fetch(`${API_URL}/scheduled-submissions?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch scheduled submissions');
      }

      setSubmissions(data.data.submissions || []);
    } catch (err) {
      console.error('Fetch scheduled submissions error:', err);
      setError(err.message || 'Failed to fetch scheduled submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubmission = async (scheduleReference) => {
    try {
      const response = await fetch(`${API_URL}/scheduled-submissions/${scheduleReference}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to cancel submission');
      }

      setSuccess('Scheduled submission cancelled successfully');
      fetchScheduledSubmissions();
    } catch (err) {
      console.error('Cancel submission error:', err);
      setError(err.message || 'Failed to cancel submission');
    }
  };

  const handleEditSubmission = (submission) => {
    setEditDialog({
      open: true,
      submission,
      newDateTime: format(new Date(submission.scheduled_at), "yyyy-MM-dd'T'HH:mm")
    });
  };

  const handleUpdateSubmission = async () => {
    try {
      const response = await fetch(`${API_URL}/scheduled-submissions/${editDialog.submission.schedule_reference}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          scheduledAt: editDialog.newDateTime
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update submission');
      }

      setSuccess('Scheduled submission updated successfully');
      setEditDialog({ open: false, submission: null, newDateTime: '' });
      fetchScheduledSubmissions();
    } catch (err) {
      console.error('Update submission error:', err);
      setError(err.message || 'Failed to update submission');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'info';
      case 'pending': return 'warning';
      case 'success': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const formatDateTime = (dateString) => {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Scheduled Submissions
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchScheduledSubmissions}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              type="date"
              value={filters.from_date}
              onChange={(e) => setFilters(prev => ({ ...prev, from_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              type="date"
              value={filters.to_date}
              onChange={(e) => setFilters(prev => ({ ...prev, to_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Submissions Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Call ID</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Scheduled At</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : submissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No scheduled submissions found
                </TableCell>
              </TableRow>
            ) : (
              submissions.map((submission) => (
                <TableRow key={submission.id}>
                  <TableCell>{submission.Call?.unique_id || submission.call_id}</TableCell>
                  <TableCell>{submission.ServiceProvider?.name || 'Unknown'}</TableCell>
                  <TableCell>{formatDateTime(submission.scheduled_at)}</TableCell>
                  <TableCell>
                    <Chip
                      label={submission.status}
                      color={getStatusColor(submission.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {submission.creator?.username || 'Unknown'}
                  </TableCell>
                  <TableCell>
                    {submission.status === 'scheduled' && (
                      <>
                        <Tooltip title="Reschedule">
                          <IconButton
                            size="small"
                            onClick={() => handleEditSubmission(submission)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleCancelSubmission(submission.schedule_reference)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, submission: null, newDateTime: '' })}>
        <DialogTitle>Reschedule Submission</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="New Date & Time"
            type="datetime-local"
            value={editDialog.newDateTime}
            onChange={(e) => setEditDialog(prev => ({ ...prev, newDateTime: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: new Date(Date.now() + 60000).toISOString().slice(0, 16)
            }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, submission: null, newDateTime: '' })}>
            Cancel
          </Button>
          <Button onClick={handleUpdateSubmission} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduledSubmissions; 